import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'

export const useDocumentStore = defineStore('document', () => {
  // State
  const documents = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const uploadProgress = ref({})

  // Getters
  const documentsByType = computed(() => {
    return documents.value.reduce((acc, doc) => {
      if (!acc[doc.type]) acc[doc.type] = []
      acc[doc.type].push(doc)
      return acc
    }, {})
  })

  const processingDocuments = computed(() => {
    return documents.value.filter(doc => 
      doc.processingStatus === 'pending' || doc.processingStatus === 'processing'
    )
  })

  // Actions
  async function fetchDocuments(projectId, sessionId, filters = {}) {
    isLoading.value = true
    error.value = null
    
    try {
      const params = new URLSearchParams()
      if (filters.type) params.append('type', filters.type)
      if (filters.tags) params.append('tags', filters.tags.join(','))
      if (filters.limit) params.append('limit', filters.limit)

      const response = await api.get(
        `/documents/projects/${projectId}/sessions/${sessionId}?${params}`
      )

      if (response.data.success) {
        documents.value = response.data.data
        console.log('Fetched documents:', documents.value)
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to fetch documents')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error fetching documents:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function uploadDocument(projectId, sessionId, file, metadata = {}) {
    const documentId = `upload_${Date.now()}`
    uploadProgress.value[documentId] = 0
    
    try {
      const formData = new FormData()
      formData.append('document', file)
      
      if (metadata.title) formData.append('title', metadata.title)
      if (metadata.tags && metadata.tags.length) {
        formData.append('tags', metadata.tags.join(','))
      }
      if (metadata.notes) formData.append('notes', metadata.notes)
      if (metadata.isPrivate !== undefined) {
        formData.append('isPrivate', metadata.isPrivate.toString())
      }

      const response = await api.post(
        `/documents/projects/${projectId}/sessions/${sessionId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            uploadProgress.value[documentId] = progress
          }
        }
      )

      if (response.data.success) {
        documents.value.push(response.data.data)
        delete uploadProgress.value[documentId]
        
        // Start polling for processing status if needed
        if (response.data.data.processingStatus === 'pending') {
          pollDocumentStatus(projectId, sessionId, response.data.data._id)
        }
        
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Upload failed')
      }
    } catch (err) {
      delete uploadProgress.value[documentId]
      error.value = err.message
      console.error('Error uploading document:', err)
      throw err
    }
  }

  async function addWebsiteLink(projectId, sessionId, linkData) {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.post(
        `/documents/projects/${projectId}/sessions/${sessionId}/website`,
        linkData
      )

      if (response.data.success) {
        documents.value.push(response.data.data)
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to add website link')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error adding website link:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function addResearchNote(projectId, sessionId, noteData) {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.post(
        `/documents/projects/${projectId}/sessions/${sessionId}/note`,
        noteData
      )

      if (response.data.success) {
        documents.value.push(response.data.data)
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to add research note')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error adding research note:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateDocument(projectId, sessionId, documentId, updates) {
    try {
      const response = await api.put(
        `/documents/projects/${projectId}/sessions/${sessionId}/documents/${documentId}`,
        updates
      )

      if (response.data.success) {
        const index = documents.value.findIndex(doc => doc._id === documentId)
        if (index !== -1) {
          documents.value[index] = { ...documents.value[index], ...response.data.data }
        }
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to update document')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error updating document:', err)
      throw err
    }
  }

  async function deleteDocument(projectId, sessionId, documentId) {
    try {
      const response = await api.delete(
        `/documents/projects/${projectId}/sessions/${sessionId}/documents/${documentId}`
      )

      if (response.data.success) {
        documents.value = documents.value.filter(doc => doc._id !== documentId)
        return true
      } else {
        throw new Error(response.data.error || 'Failed to delete document')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error deleting document:', err)
      throw err
    }
  }

  async function getDocumentStatus(projectId, sessionId, documentId) {
    try {
      const response = await api.get(
        `/documents/projects/${projectId}/sessions/${sessionId}/documents/${documentId}/status`
      )

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to get document status')
      }
    } catch (err) {
      console.error('Error getting document status:', err)
      throw err
    }
  }

  async function retryOCR(projectId, sessionId, documentId) {
    try {
      const response = await api.post(
        `/documents/projects/${projectId}/sessions/${sessionId}/documents/${documentId}/retry-ocr`
      )

      if (response.data.success) {
        const index = documents.value.findIndex(doc => doc._id === documentId)
        if (index !== -1) {
          documents.value[index] = { ...documents.value[index], ...response.data.data }
        }
        
        // Start polling for status
        pollDocumentStatus(projectId, sessionId, documentId)
        
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to retry OCR')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error retrying OCR:', err)
      throw err
    }
  }

  // Utility function to poll document processing status
  async function pollDocumentStatus(projectId, sessionId, documentId, maxAttempts = 30) {
    let attempts = 0
    
    const poll = async () => {
      if (attempts >= maxAttempts) return
      
      try {
        const status = await getDocumentStatus(projectId, sessionId, documentId)
        
        // Update document in store
        const index = documents.value.findIndex(doc => doc._id === documentId)
        if (index !== -1) {
          documents.value[index] = { ...documents.value[index], ...status }
        }
        
        if (status.processingStatus === 'processing' || status.ocrStatus === 'processing') {
          attempts++
          setTimeout(poll, 2000) // Poll every 2 seconds
        }
      } catch (err) {
        console.error('Error polling document status:', err)
      }
    }
    
    poll()
  }

  // Helper function to get document type icon
  function getDocumentTypeIcon(type) {
    const icons = {
      pdf: '📄',
      website: '🌐',
      note: '📝',
      csv: '📊',
      doc: '📄',
      docx: '📄',
      txt: '📄',
      jpeg: '🖼️',
      jpg: '🖼️',
      png: '🖼️',
      tiff: '🖼️',
      bmp: '🖼️'
    }
    return icons[type] || '📎'
  }

  // Clear state
  function clearDocuments() {
    documents.value = []
    error.value = null
    uploadProgress.value = {}
  }

  return {
    // State
    documents,
    isLoading,
    error,
    uploadProgress,
    
    // Getters
    documentsByType,
    processingDocuments,
    
    // Actions
    fetchDocuments,
    uploadDocument,
    addWebsiteLink,
    addResearchNote,
    updateDocument,
    deleteDocument,
    getDocumentStatus,
    retryOCR,
    pollDocumentStatus,
    getDocumentTypeIcon,
    clearDocuments
  }
})